// Fire-Alarm Design-Build engine (ported from MyKyah `fire-alarm-designer.js`).
// Deterministic R-2 multi-family design: inputs → device counts → BOM → Budgetary
// ROM. NOT a stamped design — labor + engineering must be reviewed by a licensed
// FA engineer / NICET III before submittal. Code basis: NFPA 72 (2022), CBC §907.

// ── Pricing (Notifier baseline, Allan's California rates) ────────────────────
export interface PriceItem { desc: string; material: number; hours: number }
type PriceKey =
  | 'facp_addressable' | 'facp_small' | 'annunciator_lcd' | 'communicator_ipdact'
  | 'battery_pair' | 'smoke_photo_addr' | 'heat_addr' | 'duct_smoke_kit'
  | 'pull_station' | 'horn_strobe_wall_75cd' | 'strobe_only_wall'
  | 'horn_strobe_ceiling' | 'speaker_strobe_voice' | 'voice_amp_panel'
  | 'waterflow_module' | 'tamper_module' | 'relay_module'
  | 'nac_booster' | 'elevator_recall_kit' | 'co_detector_addr' | 'aor_two_way'
  | 'sync_module' | 'firefighter_phone' | 'fire_pump_monitor' | 'door_holder'
  | 'ci_cable_2hr_ft' | 'wire_fplr_ft' | 'conduit_emt_ft'
  | 'engineering' | 'permit' | 'commissioning' | 'monitoring_yr1';

const PRICING: Record<PriceKey, PriceItem> = {
  facp_addressable:     { desc: 'Notifier NFS2-3030 addressable FACP', material: 5800, hours: 16 },
  facp_small:           { desc: 'Notifier NFS-320 small addressable FACP', material: 3200, hours: 12 },
  annunciator_lcd:      { desc: 'Notifier ABS-2D LCD annunciator', material: 1100, hours: 4 },
  communicator_ipdact:  { desc: 'IPDACT-2 IP/Cellular digital communicator', material: 850, hours: 4 },
  battery_pair:         { desc: 'Sealed lead-acid battery pair, sized per calc', material: 280, hours: 1.5 },
  smoke_photo_addr:     { desc: 'Notifier FSP-851 photo smoke + B710 base, addressable', material: 95, hours: 1.0 },
  heat_addr:            { desc: 'Notifier FST-851R heat 135°F rate-of-rise, addressable', material: 75, hours: 0.9 },
  duct_smoke_kit:       { desc: 'Notifier FSD-751RP duct smoke + remote test/key + indicator', material: 420, hours: 4.0 },
  pull_station:         { desc: 'Notifier NBG-12LX dual-action addressable pull station', material: 110, hours: 1.0 },
  horn_strobe_wall_75cd:{ desc: 'Notifier SR-MCH wall horn/strobe, 75 cd, low-frequency 520 Hz', material: 140, hours: 1.2 },
  strobe_only_wall:     { desc: 'Notifier SC-MCH wall strobe-only, 75 cd', material: 85, hours: 1.0 },
  horn_strobe_ceiling:  { desc: 'Notifier P2RH-SP-LF ceiling horn/strobe, 95 cd low-frequency', material: 170, hours: 1.3 },
  speaker_strobe_voice: { desc: 'Notifier SPSCWL-P speaker/strobe (voice-evac systems)', material: 195, hours: 1.4 },
  voice_amp_panel:      { desc: 'Notifier DVC-EM voice evac amplifier (high-rise / >100 sleeping rooms)', material: 4200, hours: 8 },
  waterflow_module:     { desc: 'Addressable monitor module for waterflow switch', material: 85, hours: 0.7 },
  tamper_module:        { desc: 'Addressable monitor module for tamper switch', material: 85, hours: 0.7 },
  relay_module:         { desc: 'Addressable relay module (HVAC shutdown, door release)', material: 95, hours: 0.8 },
  nac_booster:          { desc: 'Notifier ARPS-160 NAC power extender/booster (10A, 4 NAC) + battery (NFPA 72 §10)', material: 1450, hours: 5 },
  elevator_recall_kit:  { desc: 'Elevator recall interface kit (NFPA 72 §21.4, ASME A17.1, CBC §3008)', material: 1850, hours: 10 },
  co_detector_addr:     { desc: 'Addressable CO detector (CBC §915)', material: 165, hours: 1.0 },
  aor_two_way:          { desc: 'Area-of-Refuge two-way comm station + area master (CBC §1009.8, §907.5.2.3.4)', material: 950, hours: 3.5 },
  sync_module:          { desc: 'NAC strobe synchronization module (NFPA 72 §18.5.5.5.2)', material: 180, hours: 0.6 },
  firefighter_phone:    { desc: 'Firefighter two-way comm jack + warden riser (CBC §907.2.13.2, NFPA 72 §24.5)', material: 240, hours: 1.2 },
  fire_pump_monitor:    { desc: 'Fire pump supervisory interface (NFPA 72 §17.16, NFPA 20)', material: 520, hours: 4 },
  door_holder:          { desc: 'Magnetic door holder + power, rated corridor doors (NFPA 72 §21.5)', material: 145, hours: 1.0 },
  ci_cable_2hr_ft:      { desc: '2-hour CI survivable cable (high-rise voice/EVACS) [per LF]', material: 4.20, hours: 0.04 },
  wire_fplr_ft:         { desc: 'FPLR 18 AWG twisted shielded pair [per LF]', material: 0.65, hours: 0.015 },
  conduit_emt_ft:       { desc: '3/4" EMT conduit + straps + fittings [per LF]', material: 1.80, hours: 0.08 },
  engineering:          { desc: 'NICET Level III / licensed FPE design, calcs, AHJ submittal', material: 0, hours: 110 },
  permit:               { desc: 'AHJ permit fees (placeholder — varies by jurisdiction)', material: 4500, hours: 0 },
  commissioning:        { desc: 'Programming, 100% point-to-point test, AHJ witness, owner training', material: 0, hours: 32 },
  monitoring_yr1:       { desc: 'First year UL Central Station monitoring', material: 720, hours: 0 },
};

// ── Constants ───────────────────────────────────────────────────────────────
const LABOR_STANDARD = 95;     // $/hr non-prevailing
const LABOR_PW       = 145;    // $/hr CA prevailing — Sound & Communication (county-varies)
const MARKUP         = 0.22;   // 22% on subtotal — typical FA design-build margin
const CONTINGENCY    = 0.10;   // 10% design-build contingency

// Commercial addressable fire-alarm panel manufacturers. 3DTSI picks the one the
// project specifies / they are authorized to install — some brands they are NOT
// authorized to sell, so the estimator must be able to choose. `large`/`small` are
// the manufacturer's flagship networked vs. small-building panel models, shown in
// the BOM and submittal. The budgetary ROM pricing is panel-agnostic (the GM tunes
// real pricing per quote); the selection drives the basis-of-design naming.
export interface FaManufacturer { value: string; label: string; large: string; small: string }
export const FA_MANUFACTURERS: FaManufacturer[] = [
  { value: 'auto', label: 'Auto — best fit (recommended)', large: 'Addressable networked FACP', small: 'Addressable FACP' },
  { value: 'notifier', label: 'Notifier (Honeywell)', large: 'Notifier NFS2-3030', small: 'Notifier NFS-320' },
  { value: 'silentknight', label: 'Silent Knight (Honeywell)', large: 'Silent Knight 6820EVS', small: 'Silent Knight 5820XL' },
  { value: 'firelite', label: 'Fire-Lite (Honeywell)', large: 'Fire-Lite ES-200X', small: 'Fire-Lite MS-9050UD' },
  { value: 'farenhyt', label: 'Honeywell Farenhyt', large: 'Farenhyt IFP-2100ECS', small: 'Farenhyt IFP-300' },
  { value: 'gamewell', label: 'Gamewell-FCI (Honeywell)', large: 'Gamewell-FCI E3 Series', small: 'Gamewell-FCI S3 Series' },
  { value: 'simplex', label: 'Simplex (Johnson Controls)', large: 'Simplex 4100ES', small: 'Simplex 4010ES' },
  { value: 'autocall', label: 'Autocall (Johnson Controls)', large: 'Autocall A180', small: 'Autocall A084' },
  { value: 'vigilant', label: 'Vigilant (Johnson Controls)', large: 'Vigilant VM-1', small: 'Vigilant VS-1' },
  { value: 'est', label: 'Edwards / EST (Carrier)', large: 'EST4', small: 'EST iO1000' },
  { value: 'kidde', label: 'Kidde (Carrier)', large: 'Kidde VS-4', small: 'Kidde VS-1' },
  { value: 'siemens', label: 'Siemens', large: 'Siemens Desigo FS40', small: 'Siemens Cerberus PRO FC922' },
  { value: 'bosch', label: 'Bosch', large: 'Bosch FPA-5000', small: 'Bosch FPA-1200' },
  { value: 'mircom', label: 'Mircom', large: 'Mircom FX-3500', small: 'Mircom FX-2000' },
  { value: 'secutron', label: 'Secutron', large: 'Secutron MR-2900', small: 'Secutron MR-2300' },
  { value: 'hochiki', label: 'Hochiki', large: 'Hochiki FireNET Plus', small: 'Hochiki FN-2127' },
  { value: 'potter', label: 'Potter', large: 'Potter IPA-4000', small: 'Potter PFC-4064' },
  { value: 'napco', label: 'NAPCO Firewolf', large: 'NAPCO GEMC-FW', small: 'NAPCO GEMC-FW' },
  { value: 'other', label: 'Other / as specified (verify authorization)', large: 'Addressable networked FACP (as specified)', small: 'Addressable FACP (as specified)' },
];

// ── Inputs ──────────────────────────────────────────────────────────────────
export interface DesignInput {
  projectName?: string;
  cityAhj?: string;
  faSystem?: string;   // FaManufacturer.value
  occupancy?: 'r2_apartments' | 'r2_condos';
  units: number;
  stories: number;
  heightFt?: number;
  grossSf?: number;
  sprinklerType?: 'none' | '13' | '13R' | '13D';
  commonSf?: number;
  corridorLf?: number;
  stairs?: number;
  exits?: number;
  hvacUnits?: number;
  elevatorLobbies?: number;
  mechanicalRooms?: number;
  electricalRooms?: number;
  atticAccess?: boolean;
  prevailingWage?: boolean;
  firePump?: boolean;
  chuteRooms?: number;
  conduitFraction?: number;
}

export interface BomLine { key: string; description: string; quantity: number; unit_material: number; unit_hours: number; ext_material: number; ext_hours: number }

export interface DesignResult {
  input: DesignInput;
  counts: Record<string, number>;
  bom: BomLine[];
  panel: string;        // selected manufacturer panel (basis of design)
  laborRate: number;
  totals: { material: number; hours: number; laborCost: number; engineering: number; permit: number; sheets: number; subtotal: number; markup: number; contingency: number; total: number; perUnit: number };
  flags: { requiresSystem: boolean; isHighRise: boolean; requiresVoiceEvac: boolean; isLargePanel: boolean };
}

// ── Engine ──────────────────────────────────────────────────────────────────
export function computeFaDesign(input: DesignInput): DesignResult {
  const units = Math.max(1, +input.units || 0);
  const stories = Math.max(1, +input.stories || 1);
  const heightFt = +input.heightFt || stories * 11;
  const grossSf = +input.grossSf || units * 950;
  const commonSf = +input.commonSf || Math.round(grossSf * 0.18);
  const corridorLf = +input.corridorLf || Math.round(Math.sqrt(grossSf) * 1.2 * stories);
  const stairs = +input.stairs || Math.max(2, Math.ceil(units / 60));
  const exits = +input.exits || Math.max(2, stairs);
  const hvacUnits = +input.hvacUnits || Math.ceil(units / 8);
  const elevatorLobbies = +input.elevatorLobbies || (stories > 1 ? stories : 0);
  const mechanicalRooms = +input.mechanicalRooms || Math.max(2, Math.ceil(units / 25));
  const electricalRooms = +input.electricalRooms || Math.max(2, Math.ceil(units / 30));
  const chuteRooms = +input.chuteRooms || Math.max(1, Math.ceil(stories / 3));
  const sprinklered = !!(input.sprinklerType && input.sprinklerType !== 'none');

  const requiresSystem = units >= 16 || stories >= 3 || sprinklered;
  const isHighRise = heightFt >= 75;
  const conservativeVoiceOver = units >= 100;
  const requiresVoiceEvac = isHighRise || conservativeVoiceOver;
  const isLargePanel = units >= 80 || stories >= 5;

  const counts: Record<string, number> = {};
  counts.facp = isLargePanel ? 1 : 1; // 1 panel either way; type chosen below
  const facpKey: PriceKey = isLargePanel ? 'facp_addressable' : 'facp_small';
  counts.annunciator = 1; counts.communicator = 1; counts.batteries = 1;
  counts.voiceAmp = requiresVoiceEvac ? 1 : 0;

  // Common-area smoke detection (NFPA 72 §17.7 nominal 30 ft, 800 sf conservative)
  counts.smokeCommon =
    Math.ceil(commonSf / 800) +
    elevatorLobbies +
    stories +                                  // elevator machine room / top-of-hoistway
    electricalRooms +
    stairs * Math.max(1, Math.ceil(stories / 3)) +
    chuteRooms;

  // Corridor smoke detection (CBC §907.2.9 — sprinklered exception)
  counts.smokeCorridor = sprinklered ? stairs * stories : Math.ceil(corridorLf / 30);

  // Heat detection — mech / kitchens / attic / shunt-trip lobby
  counts.heat =
    mechanicalRooms +
    Math.ceil(units / 25) +
    (input.atticAccess ? stories : 0) +
    (sprinklered ? Math.max(1, elevatorLobbies > 0 ? Math.ceil(stories / 7) : 0) : 0);

  // Duct smoke (NFPA 90A — supply >2000 CFM AND return paths)
  counts.duct = Math.ceil(hvacUnits * 1.5);

  // Pull stations (CBC §907.4.2, NFPA 72 §17.14)
  counts.pull = exits * stories + stairs * stories + 1;

  // Notification appliances
  if (requiresVoiceEvac) {
    counts.horn_strobe = 0;
    counts.strobe_only = 0;
    counts.speaker_strobe = units + Math.ceil(commonSf / 1200) + Math.ceil(corridorLf / 50) + stairs * stories;
  } else {
    counts.horn_strobe = units + Math.ceil(corridorLf / 50) + Math.ceil(commonSf / 1200) + stairs * stories;
    counts.strobe_only = Math.ceil(units * 0.35) + Math.ceil(commonSf / 2000);
    counts.speaker_strobe = 0;
  }

  // Strobe sync (NFPA 72 §18.5.5.5.2)
  const totalStrobes = counts.horn_strobe + counts.strobe_only + counts.speaker_strobe;
  counts.sync = Math.max(stories, Math.ceil(totalStrobes / 20));

  // Sprinkler supervision
  if (sprinklered) {
    counts.waterflow = Math.max(2, stories * 2);
    counts.tamper = Math.max(2, stories * 2 + 2);
    counts.firePump = input.firePump ? 1 : 0;
  } else {
    counts.waterflow = 0; counts.tamper = 0; counts.firePump = 0;
  }

  // HVAC shutdown + interlocks
  counts.relay = hvacUnits + Math.ceil(hvacUnits * 0.5);

  // Elevator recall kits (NFPA 72 §21.4)
  counts.elevatorRecall = elevatorLobbies > 0 ? Math.max(1, Math.ceil(elevatorLobbies / stories)) : 0;

  // CO detection (CBC §915)
  counts.co = mechanicalRooms + Math.ceil(stories / 2);

  // Area-of-Refuge (CBC §1009.1 sprinkler exception)
  counts.aor = sprinklered
    ? 0
    : ((isHighRise || stories >= 4) ? stairs * Math.max(1, stories - 1) : (stories >= 2 ? stairs : 0));

  // Firefighter two-way (high-rise only — CBC §907.2.13.2)
  counts.ffComm = isHighRise ? stairs * stories + elevatorLobbies + 1 : 0;

  // Magnetic door holders
  counts.doorHolder = Math.max(0, stairs * stories - stairs);

  // NAC boosters (≈1 per 25 appliances, min 1 per 2 stories)
  const notifTotal = counts.horn_strobe + counts.strobe_only + counts.speaker_strobe;
  counts.nacBooster = Math.max(Math.ceil(stories / 2), Math.ceil(notifTotal / 25));

  // Battery sets — FACP + voice amp + 1 spare (boosters self-include batteries)
  counts.batteries = 1 + counts.voiceAmp + 1;

  // Wire + conduit
  const totalDevices =
    counts.smokeCommon + counts.smokeCorridor + counts.heat + counts.duct + counts.pull +
    counts.horn_strobe + counts.strobe_only + counts.speaker_strobe +
    counts.waterflow + counts.tamper + counts.relay + counts.co +
    counts.aor + counts.ffComm + counts.doorHolder + counts.firePump +
    counts.elevatorRecall * 4;
  counts.wire_lf = totalDevices * 60 + corridorLf * 2;
  const conduitFrac = (input.conduitFraction != null && Number(input.conduitFraction))
    || (input.sprinklerType === '13R' ? 0.20 : (isHighRise ? 0.45 : 0.30));
  counts.conduit_lf = Math.round(counts.wire_lf * conduitFrac);
  counts.ciCable_lf = (requiresVoiceEvac && isHighRise) ? Math.round(stories * 220 + corridorLf * 0.5) : 0;

  // ── Build BOM ─────────────────────────────────────────────────────────────
  const bom: BomLine[] = [];
  const add = (key: PriceKey, qty: number, overrideDesc?: string) => {
    if (!qty || qty <= 0) return;
    const item = PRICING[key];
    const ext_material = Math.round(qty * item.material * 100) / 100;
    const ext_hours = Math.round(qty * item.hours * 100) / 100;
    bom.push({ key, description: overrideDesc || item.desc, quantity: qty, unit_material: item.material, unit_hours: item.hours, ext_material, ext_hours });
  };
  // Name the panel after the selected manufacturer (basis of design).
  const mfr = FA_MANUFACTURERS.find((m) => m.value === (input.faSystem || 'auto')) ?? FA_MANUFACTURERS[0]!;
  const panelDesc = `${isLargePanel ? mfr.large : mfr.small} addressable fire alarm control panel`;
  add(facpKey, 1, panelDesc);
  add('annunciator_lcd', counts.annunciator!);
  add('communicator_ipdact', counts.communicator!);
  add('battery_pair', counts.batteries!);
  if (counts.voiceAmp) add('voice_amp_panel', counts.voiceAmp);
  if (counts.nacBooster) add('nac_booster', counts.nacBooster);
  add('smoke_photo_addr', counts.smokeCommon!);
  add('smoke_photo_addr', counts.smokeCorridor!, `${PRICING.smoke_photo_addr.desc} — DWELLING-UNIT CORRIDOR`);
  add('heat_addr', counts.heat!);
  add('duct_smoke_kit', counts.duct!);
  add('co_detector_addr', counts.co!);
  add('pull_station', counts.pull!);
  if (counts.horn_strobe) add('horn_strobe_wall_75cd', counts.horn_strobe);
  if (counts.strobe_only) add('strobe_only_wall', counts.strobe_only);
  if (counts.speaker_strobe) add('speaker_strobe_voice', counts.speaker_strobe);
  if (counts.sync) add('sync_module', counts.sync);
  if (counts.waterflow) add('waterflow_module', counts.waterflow);
  if (counts.tamper) add('tamper_module', counts.tamper);
  if (counts.firePump) add('fire_pump_monitor', counts.firePump);
  if (counts.relay) add('relay_module', counts.relay);
  if (counts.elevatorRecall) add('elevator_recall_kit', counts.elevatorRecall);
  if (counts.aor) add('aor_two_way', counts.aor);
  if (counts.ffComm) add('firefighter_phone', counts.ffComm);
  if (counts.doorHolder) add('door_holder', counts.doorHolder);
  add('wire_fplr_ft', counts.wire_lf!);
  if (counts.ciCable_lf) add('ci_cable_2hr_ft', counts.ciCable_lf);
  add('conduit_emt_ft', counts.conduit_lf!);
  add('engineering', 1);
  add('permit', 1);
  add('commissioning', 1);
  add('monitoring_yr1', 1);

  // ── Totals ────────────────────────────────────────────────────────────────
  const laborRate = input.prevailingWage ? LABOR_PW : LABOR_STANDARD;
  const totalMaterial = bom.reduce((s, b) => s + b.ext_material, 0);
  const totalHours = bom.reduce((s, b) => s + b.ext_hours, 0);
  const laborCost = totalHours * laborRate;

  // Estimated stamped-drawing sheet count (cover/index/code/legend/riser/sequence/
  // schedule + per-floor plans + calc sheets + device-driven plan sheets). 20–100
  // typical per the AHJ package scope.
  const totalDevicesForSheets =
    counts.smokeCommon + counts.smokeCorridor + counts.heat + counts.duct + counts.pull +
    counts.horn_strobe + counts.strobe_only + counts.speaker_strobe;
  const sheets = Math.max(12, Math.min(100,
    10 + Math.ceil(stories * 1.5) + Math.ceil(totalDevicesForSheets / 120)));

  // Licensed PE / NICET-III stamped, permit-ready design package — what 3DTSI pays
  // the engineer of record to produce + seal the full AHJ submittal (drawings,
  // calcs, cut sheets). Budgetary basis: the greater of a floor, a per-sheet rate,
  // or ~10% of installed cost. AHJ permit fee is a separate pass-through allowance.
  const engineering = Math.max(6000, sheets * 450, 0.10 * (totalMaterial + laborCost));
  const permit = 4500;

  const subtotal = totalMaterial + laborCost + engineering + permit;
  const markup = subtotal * MARKUP;
  const contingency = (subtotal + markup) * CONTINGENCY;
  const total = subtotal + markup + contingency;

  return {
    input,
    counts: { ...counts, _conduitFracPct: Math.round(conduitFrac * 100) },
    bom,
    panel: panelDesc,
    laborRate,
    totals: {
      material: Math.round(totalMaterial),
      hours: Math.round(totalHours),
      laborCost: Math.round(laborCost),
      engineering: Math.round(engineering),
      permit: Math.round(permit),
      sheets,
      subtotal: Math.round(subtotal),
      markup: Math.round(markup),
      contingency: Math.round(contingency),
      total: Math.round(total),
      perUnit: Math.round(total / units),
    },
    flags: { requiresSystem, isHighRise, requiresVoiceEvac, isLargePanel },
  };
}

export function fmtMoney(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
